<template>
  <b-modal
    ref="modal"
    :id="modalId"
    title="Choose from gallery"
    size="lg"
    body-class="p-0"
    centered
    @show="onShow"
    @hidden="onHidden"
  >
    <div class="gallery-body">
      <div v-if="loading" class="gallery-loading">
        <b-spinner variant="primary" />
      </div>
      <div v-else-if="images.length === 0" class="gallery-empty">No images available.</div>
      <div v-else class="gallery-grid">
        <button
          v-for="image in images"
          :key="image.s3Key"
          type="button"
          :class="['gallery-item', { 'gallery-item--selected': selectedS3Key === image.s3Key }]"
          @click="selectedS3Key = image.s3Key"
        >
          <img :src="image.url" :alt="image.filename" class="gallery-item__image" />
        </button>
      </div>
    </div>

    <template #modal-footer>
      <b-button variant="outline-primary" @click="onCancel">Cancel</b-button>
      <b-button variant="primary" :disabled="!selectedS3Key" @click="onConfirm">Use image</b-button>
    </template>
  </b-modal>
</template>

<script setup>
import { ref, computed } from 'vue';
import ApiClient from '@/ApiClient';

const props = defineProps({
  modalId: { type: String, required: true },
  imageType: { type: String, required: true, validator: v => ['banner', 'logo'].includes(v) },
});

const emit = defineEmits(['select']);

const modal = ref(null);
const loading = ref(false);
const images = ref([]);
const selectedS3Key = ref('');

const selectedImage = computed(() => images.value.find(img => img.s3Key === selectedS3Key.value));

const onShow = async () => {
  loading.value = true;
  selectedS3Key.value = '';
  try {
    const data = await ApiClient.getRecognitionGalleryImages();
    const genericImages = data?.generic?.[props.imageType] || {};
    images.value = Object.entries(genericImages).map(([filename, url]) => ({
      filename,
      url,
      s3Key: `recognition/${props.imageType}/${filename}`,
    }));
  } catch {
    images.value = [];
  } finally {
    loading.value = false;
  }
};

const onHidden = () => {
  selectedS3Key.value = '';
};

const onCancel = () => {
  modal.value?.hide();
};

const onConfirm = () => {
  if (selectedImage.value) {
    emit('select', { url: selectedImage.value.url, s3Key: selectedImage.value.s3Key });
  }
  modal.value?.hide();
};
</script>

<style scoped>
:deep(.modal-title) {
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.5rem;
  width: 100%;
  text-align: center;
  margin-left: 1.25rem;
}

:deep(.modal-header) {
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
}

.gallery-body {
  padding: 1.5rem;
  border-bottom: 1px solid var(--palette-color-default-lighten-90);
  min-height: 12rem;
}

.gallery-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
}

.gallery-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 12rem;
  color: var(--palette-color-default-lighten-50);
  font-size: 0.875rem;
}

.gallery-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.75rem;
}

.gallery-item {
  position: relative;
  border: 3px solid transparent;
  border-radius: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  background: var(--palette-color-core-1-lighten-90);
  aspect-ratio: 16 / 9;
  transition: border-color 0.15s ease;
}

.gallery-item:hover {
  border-color: var(--palette-color-default-lighten-70);
}

.gallery-item--selected {
  border-color: var(--palette-color-brand-primary);
}

.gallery-item--selected:hover {
  border-color: var(--palette-color-brand-primary);
}

.gallery-item__image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
}
</style>
